import React, { useState, useEffect, useCallback } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Bot, CheckCircle, Clock, RefreshCw, Zap, TrendingUp, AlertTriangle, Activity, Mic, Target
} from 'lucide-react';

/**
 * Live AI Knowledge / Activation panel.
 *
 * Replaces the former "AI Training" tab, whose metrics (94.7% accuracy, "retrain in 4 hours", etc.)
 * were fabricated — no such mechanism exists in the backend. VERIFIED backend truth: AI Chat
 * (ai_chat_routes.py) and AI Voice (ai_voice_routes.py) read approved `solutions` LIVE on every
 * customer message/turn (status == "approved"). There is no retrain job, no embedding index, no
 * cache, no 4-hour cycle. An approval is effective on the very next customer interaction.
 *
 * This panel shows ONLY real data pulled from GET /api/v1/solutions/{brandId} (all statuses), and
 * states the activation model honestly. No fake metrics, no mock.
 */
const REVIEW_SUCCESS_THRESHOLD = 50; // approved + used but under this success% may need review

const LiveAiKnowledgePanel = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const businessId = currentBusiness?.brandId;
  const businessName = currentBusiness?.businessName;

  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/solutions/${businessId}`);
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setSolutions(Array.isArray(data.solutions) ? data.solutions : []);
    } catch (err) {
      setError(err.message || t('sb.live.loadFailedToast'));
      setSolutions([]);
      toast({ title: t('sb.live.loadFailedToast'), description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [businessId, makeAuthenticatedRequest, toast, t]);

  useEffect(() => { load(); }, [load]);

  const byStatus = (s) => solutions.filter((x) => x.status === s);
  const approved = byStatus('approved');
  const pending = byStatus('pending_approval');
  const drafts = solutions.filter((x) => x.status === 'draft' || x.status === 'suggested');
  const inactive = solutions.filter((x) => ['rejected', 'revoked', 'archived'].includes(x.status));

  const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : 0);
  const totalUsage = approved.reduce((a, s) => a + num(s.usage_count), 0);
  const usedApproved = approved.filter((s) => num(s.usage_count) > 0);
  const avgSuccess = usedApproved.length
    ? Math.round(usedApproved.reduce((a, s) => a + num(s.success_rate), 0) / usedApproved.length)
    : null;

  const approvedTime = (s) => s.approved_at || s.reviewed_at || s.last_updated || s.created_at;
  const lastApproved = approved
    .map(approvedTime)
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;

  const topUsed = [...approved].sort((a, b) => num(b.usage_count) - num(a.usage_count)).filter((s) => num(s.usage_count) > 0).slice(0, 5);
  const recentlyApproved = [...approved].sort((a, b) => String(approvedTime(b) || '').localeCompare(String(approvedTime(a) || ''))).slice(0, 5);
  const lowSuccess = usedApproved.filter((s) => num(s.success_rate) < REVIEW_SUCCESS_THRESHOLD).sort((a, b) => num(a.success_rate) - num(b.success_rate)).slice(0, 5);

  const StatCard = ({ label, value, icon: Icon, color, sub }) => (
    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
          </div>
          <Icon className={`h-8 w-8 ${color}`} />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Honest activation model — the required, verified statement */}
      <Card className="border-green-200 bg-gradient-to-r from-green-50 to-blue-50 shadow-lg">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Zap className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-900 mb-1">{t('sb.live.approvalsImmediate')}</h3>
              <p className="text-sm text-green-800">
                {t('sb.live.approvalsBlurb')}
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <Badge className="bg-white text-green-700 border border-green-200"><Bot className="h-3 w-3 mr-1" />{t('sb.live.aiChatLive')}</Badge>
                <Badge className="bg-white text-green-700 border border-green-200"><Mic className="h-3 w-3 mr-1" />{t('sb.live.aiVoiceLive')}</Badge>
                {businessName && <span className="text-xs text-green-700">{t('sb.live.business', { name: businessName })}</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Activity className="h-5 w-5 text-purple-600" />{t('nav.liveAiKnowledge')}</h2>
          <p className="text-sm text-gray-600">{t('page.liveAi.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}
        </Button>
      </div>

      {loading ? (
        <Card className="bg-white/70 border-0 shadow-lg"><CardContent className="p-12 text-center">
          <RefreshCw className="h-10 w-10 text-purple-600 mx-auto mb-3 animate-spin" />
          <p className="text-gray-600">{t('sb.live.loading')}</p>
        </CardContent></Card>
      ) : error ? (
        <Card className="border-red-200"><CardContent className="p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">{t('sb.live.loadError')}</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={load}>{t('sb.live.tryAgain')}</Button>
        </CardContent></Card>
      ) : (
        <>
          {/* Real counts */}
          <div className="grid md:grid-cols-4 gap-6">
            <StatCard label={t('sb.live.liveInAi')} value={approved.length} icon={CheckCircle} color="text-green-600" sub={t('sb.live.usedByAiNow')} />
            <StatCard label={t('sb.live.pendingApproval')} value={pending.length} icon={Clock} color="text-amber-600" sub={t('sb.live.notYetUsed')} />
            <StatCard label={t('sb.live.draftsSuggested')} value={drafts.length} icon={Bot} color="text-blue-600" sub={t('sb.live.internalOnly')} />
            <StatCard label={t('sb.live.inactive')} value={inactive.length} icon={AlertTriangle} color="text-gray-500" sub={t('sb.live.rejectedRevoked')} />
          </div>

          {/* Real usage stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <StatCard label={t('sb.live.totalUsage')} value={totalUsage} icon={TrendingUp} color="text-purple-600" sub={t('sb.live.realUsageSum')} />
            <StatCard label={t('sb.live.avgSuccess')} value={avgSuccess === null ? '—' : `${avgSuccess}%`} icon={Target} color="text-green-600" sub={avgSuccess === null ? t('sb.live.noUsageYet') : t('sb.live.acrossUsed')} />
            <StatCard label={t('sb.live.lastApproved')} value={lastApproved ? formatInTenantTz(lastApproved, { dateStyle: 'medium' }) : '—'} icon={Clock} color="text-blue-600" sub={lastApproved ? t('sb.live.effectiveImmediately') : t('sb.live.noApprovalsYet')} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <ListCard title={t('sb.live.topUsed')} icon={TrendingUp} empty={t('sb.live.topUsedEmpty')} items={topUsed}
              right={(s) => <Badge variant="outline">{t('sb.usesLower', { count: num(s.usage_count) })}</Badge>} />
            <ListCard title={t('sb.live.recentlyApproved')} icon={CheckCircle} empty={t('sb.live.recentlyApprovedEmpty')} items={recentlyApproved}
              right={(s) => <span className="text-xs text-gray-500">{formatInTenantTz(approvedTime(s), { dateStyle: 'medium' })}</span>} />
          </div>

          <ListCard
            title={t('sb.live.lowSuccess')}
            icon={AlertTriangle}
            empty={t('sb.live.lowSuccessEmpty')}
            items={lowSuccess}
            right={(s) => <Badge className="bg-red-100 text-red-700">{num(s.success_rate)}%</Badge>}
            hint={t('sb.live.lowSuccessHint', { threshold: REVIEW_SUCCESS_THRESHOLD })}
          />
        </>
      )}
    </div>
  );
};

const ListCard = ({ title, icon: Icon, items, right, empty, hint }) => {
  const { t } = useLanguage();
  return (
  <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
    <CardHeader>
      <CardTitle className="text-base flex items-center gap-2"><Icon className="h-4 w-4 text-gray-500" />{title}</CardTitle>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </CardHeader>
    <CardContent>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-gray-500 py-4 text-center">{empty}</p>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{s.title || t('sb.live.untitled')}</div>
                <div className="text-xs text-gray-500 truncate">{s.category || t('sb.live.uncategorized')}</div>
              </div>
              <div className="ml-3 flex-shrink-0">{right(s)}</div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default LiveAiKnowledgePanel;
